import apiService from './api';

/**
 * Get all public courses (for list view)
 * @returns {Promise<Array>} - Array of courses
 */
export const getPublicCourses = async () => {
  try {
    const appId = 1;
    
    // Get the auth token
    const token = localStorage.getItem('token_admin');
    // Get the current domain
    const domain = window.location.hostname;
    
    const headers = {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-App-Domain': domain,
      'X-SaltMarsh-App-Context': 'admin'
    };
    
    const response = await apiService.get(`/apps/${appId}/courses`, {}, headers);
    
    if (response) {
      let courses = [];
      if (Array.isArray(response)) {
        courses = response;
      } else if (response.data && Array.isArray(response.data)) {
        courses = response.data;
      } else if (response.courses && Array.isArray(response.courses)) {
        courses = response.courses;
      }
      
      return courses;
    }
    
    return [];
  } catch (error) {
    return [];
  }
};

// Mock courses for testing when API fails
const getMockCourses = () => {
  return [
    {
      id: 1,
      title: "Introduction to the Bible",
      description: "A beginner's guide to understanding the Bible's structure, themes, and message.",
      image_url: "https://via.placeholder.com/300x200?text=Bible+Intro",
      is_published: true
    },
    {
      id: 2,
      title: "The Life of Jesus",
      description: "Explore the life, teachings, and ministry of Jesus Christ through the Gospels.",
      image_url: "https://via.placeholder.com/300x200?text=Jesus",
      is_published: true
    },
    {
      id: 3,
      title: "Old Testament Heroes",
      description: "Learn about the faith journeys of key figures from the Old Testament.",
      image_url: "https://via.placeholder.com/300x200?text=OT+Heroes",
      is_published: true
    }
  ];
};

// Define a mapping of string app names to numeric IDs
const APP_ID_MAP = {
  'the-faithful-storyteller': 1, // Assuming ID 1 for this app
  'grace-community-church': 2,
  '242-network': 3,
  'test-business': 4,
  'readtheword': 5,
  'demo-app': 6,
  'saltmarsh-admin': 7,
  'admin': 8
};

// Helper function to get numeric ID from app name
const getNumericAppId = (appName) => {
  if (!appName) return 1; // Default to ID 1 if no name provided
  
  // If appName is already a number, return it
  if (!isNaN(parseInt(appName))) {
    return parseInt(appName);
  }
  
  // Look up in the map, default to 1 if not found
  return APP_ID_MAP[appName] || 1;
};

/**
 * Get all courses for an app
 * @param {number} appId - The ID of the app
 * @returns {Promise<Array>} - Array of courses
 */
export const getCourses = async (appId) => {
  try {
    const numericAppId = getNumericAppId(appId);
    const response = await apiService.get(`/apps/${numericAppId}/courses`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a course by ID
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @returns {Promise<Object>} - Course object
 */
export const getCourse = async (appId, courseId) => {
  try {
    const numericAppId = getNumericAppId(appId);
    const response = await apiService.get(`/apps/${numericAppId}/courses/${courseId}`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new course
 * @param {number} appId - The ID of the app
 * @param {Object} courseData - Course data
 * @returns {Promise<Object>} - Created course
 */
export const createCourse = async (appId, courseData) => {
  try {
    const numericAppId = getNumericAppId(appId);
    const response = await apiService.post(`/apps/${numericAppId}/courses`, courseData);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a course
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {Object} courseData - Course data
 * @returns {Promise<Object>} - Updated course
 */
export const updateCourse = async (appId, courseId, courseData) => {
  try {
    const numericAppId = getNumericAppId(appId);
    const response = await apiService.put(`/apps/${numericAppId}/courses/${courseId}`, courseData);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a course
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @returns {Promise<Object>} - Deleted course
 */
export const deleteCourse = async (appId, courseId) => {
  try {
    const numericAppId = getNumericAppId(appId);
    const response = await apiService.delete(`/apps/${numericAppId}/courses/${courseId}`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all lessons for a course
 * @param {number} courseId - The ID of the course
 * @returns {Promise<Array>} - Array of lessons
 */
export const getLessons = async (courseId) => {
  try {
    const response = await apiService.get(`/courses/${courseId}/lessons`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a lesson by ID
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @returns {Promise<Object>} - Lesson object
 */
export const getLesson = async (appId, courseId, lessonId) => {
  try {
    const response = await apiService.get(`/lessons/${lessonId}`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new lesson
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {Object} lessonData - Lesson data
 * @returns {Promise<Object>} - Created lesson
 */
export const createLesson = async (appId, courseId, lessonData) => {
  try {
    const response = await apiService.post(`/courses/${courseId}/lessons`, lessonData);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a lesson
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @param {Object} lessonData - Lesson data
 * @returns {Promise<Object>} - Updated lesson
 */
export const updateLesson = async (appId, courseId, lessonId, lessonData) => {
  try {
    const response = await apiService.put(`/lessons/${lessonId}`, lessonData);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a lesson
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @returns {Promise<Object>} - Deleted lesson
 */
export const deleteLesson = async (appId, courseId, lessonId) => {
  try {
    const response = await apiService.delete(`/lessons/${lessonId}`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Reorder lessons in a course
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {Array<number>} lessonIds - Array of lesson IDs in desired order
 * @returns {Promise<Array>} - Updated lessons array
 */
export const reorderLessons = async (appId, courseId, lessonIds) => {
  try {
    const response = await apiService.post(`/courses/${courseId}/lessons/reorder`, { lessonIds });
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload a video for a lesson
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @param {File} videoFile - The video file to upload
 * @param {Function} progressCallback - Callback function for upload progress
 * @returns {Promise<Object>} - Updated lesson with video URL
 */
export const uploadLessonVideo = async (appId, courseId, lessonId, videoFile, progressCallback) => {
  try {
    // Create a FormData object to send the file
    const formData = new FormData();
    formData.append('video', videoFile);
    
    // Get the auth token
    const token = localStorage.getItem('token_admin');
    // Get the current domain
    const domain = window.location.hostname;
    
    const headers = {
      'Authorization': token ? `Bearer ${token}` : '',
      'X-App-Domain': domain,
      'X-SaltMarsh-App-Context': 'admin'
    };
    
    // Create a fetch request with progress tracking
    const xhr = new XMLHttpRequest();
    
    // Set up a promise to handle the response
    const promise = new Promise((resolve, reject) => {
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response.data || response);
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`HTTP Error: ${xhr.status}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.upload.onprogress = function(event) {
        if (event.lengthComputable && progressCallback) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          progressCallback(percentComplete);
        }
      };
    });
    
    // Use the correct endpoint format according to the API documentation
    xhr.open('POST', `/api/lessons/${lessonId}/upload-video`);
    
    // Set headers
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.setRequestHeader('X-App-Domain', domain);
    xhr.setRequestHeader('X-SaltMarsh-App-Context', 'admin');
    
    // Send the request
    xhr.send(formData);
    
    return promise;
  } catch (error) {
    throw error;
  }
};

/**
 * Get progress information for a lesson
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @returns {Promise<Object>} - Progress information
 */
export const getLessonProgress = async (appId, courseId, lessonId) => {
  try {
    const response = await apiService.get(`/lessons/${lessonId}/progress`);
    return response.data || response;
  } catch (error) {
    return null;
  }
};

/**
 * Mark a lesson as complete
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @returns {Promise<Object>} - Updated progress information
 */
export const markLessonComplete = async (appId, courseId, lessonId) => {
  try {
    const response = await apiService.post(`/lessons/${lessonId}/complete`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

/**
 * Mark a lesson as not complete
 * @param {number} appId - The ID of the app
 * @param {number} courseId - The ID of the course
 * @param {number} lessonId - The ID of the lesson
 * @returns {Promise<Object>} - Updated progress information
 */
export const markLessonIncomplete = async (appId, courseId, lessonId) => {
  try {
    const response = await apiService.post(`/lessons/${lessonId}/uncomplete`);
    return response.data || response;
  } catch (error) {
    throw error;
  }
};

const courseService = {
  getCourses,
  getCourse,
  createCourse,
  updateCourse,
  deleteCourse,
  getLessons,
  getLesson,
  createLesson,
  updateLesson,
  deleteLesson,
  reorderLessons,
  uploadLessonVideo,
  getLessonProgress,
  markLessonComplete,
  markLessonIncomplete
};

export default courseService; 